import { useGrowlStoreActions } from "PFApp/stores/growl_store";
import { useSession } from "PFApp/use_session";
import { fetchCurrentProfile } from "PFCore/services/current_profile";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useIdleTimer } from "react-idle-timer";

const NOTICE_WINDOW = 15 * 1000;
const DEBOUNCE_ON_ACTION_WINDOW = 500;

export const useLiveSessionCheck = (ttl: number) => {
  const [active, setActive] = useState(true);
  const timeoutId = useRef<number | null>(null);
  const { showGrowl, hideGrowl } = useGrowlStoreActions();
  const { t } = useTranslation();

  const { expireSession } = useSession();

  const handleOnIdle = () => setActive(false);

  const handleOnActive = () => {
    setActive(true);
    fetchCurrentProfile(); // Make request to API to stay alive
  };

  const handleOnAction = () => setActive(true);

  const idleTimer = useIdleTimer({
    disabled: !ttl,
    timeout: ttl ? ttl - NOTICE_WINDOW : undefined,
    onIdle: handleOnIdle,
    onActive: handleOnActive,
    onAction: handleOnAction,
    debounce: DEBOUNCE_ON_ACTION_WINDOW,
    crossTab: true, // prevents logging out active tab by inactive tabs idle event
    syncTimers: 500,
    leaderElection: true
  });

  useEffect(() => {
    const id = "live-session-check";
    if (!active) {
      timeoutId.current = window.setTimeout(() => {
        if (!active) {
          expireSession({ error: "sign_in.session_inactive", skipRevoke: !idleTimer.isLeader() });
        }
      }, NOTICE_WINDOW);
      showGrowl({
        id,
        kind: "alert",
        message: t("signUpSignIn.inactivityWarning"),
        ttl: NOTICE_WINDOW
      });
    }
    return () => {
      hideGrowl(id);
      timeoutId.current && window.clearTimeout(timeoutId.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);
};
